import { GuildEventsDomainError } from "./errors"
import {
  WWM_EVENT_ROLE_CATALOG,
  type GuildEventKind,
  type GuildEventRsvpState,
  type GuildEventStatus,
  type GuildEventSyncStatus,
  type WwmEventRoleKey,
} from "./types"

export type GuildEventKindConfiguration = {
  eventKind: GuildEventKind
  eventRoles: readonly WwmEventRoleKey[]
  signupCloseAt: Date | null
  startAt: Date
  rsvpEnabled?: boolean
}

export type GuildEventLifecycleInput = GuildEventKindConfiguration & {
  status: GuildEventStatus
}

export const assertEventKindConfiguration = (
  event: GuildEventKindConfiguration,
): void => {
  if (event.eventKind === "attendance") {
    if (event.eventRoles.length !== 0 || event.signupCloseAt !== null) {
      throw new GuildEventsDomainError(
        "EVENT_KIND_CONFIGURATION_INVALID",
        "Attendance events require no roles and no signup close time",
      )
    }
    return
  }

  if (event.eventRoles.length === 0 || event.signupCloseAt === null) {
    throw new GuildEventsDomainError(
      "EVENT_KIND_CONFIGURATION_INVALID",
      "Raid events require roles and a signup close time",
    )
  }
  if (event.signupCloseAt.getTime() >= event.startAt.getTime()) {
    throw new GuildEventsDomainError(
      "EVENT_INVALID_TIME_WINDOW",
      "Signup close time must be earlier than event start time",
    )
  }
}

export const isSignupOpen = (
  event: GuildEventLifecycleInput,
  now: Date,
): boolean => {
  if (event.status !== "scheduled") return false
  if (event.eventKind === "attendance") {
    return now.getTime() < event.startAt.getTime()
  }
  return (
    event.signupCloseAt !== null &&
    now.getTime() < event.signupCloseAt.getTime()
  )
}

export const deriveLifecycleStatus = (
  event: GuildEventKindConfiguration,
  now: Date,
): "scheduled" | "closed" | "started" => {
  if (now.getTime() >= event.startAt.getTime()) return "started"
  if (
    event.eventKind === "raid" &&
    event.signupCloseAt !== null &&
    now.getTime() >= event.signupCloseAt.getTime()
  ) {
    return "closed"
  }
  return "scheduled"
}

export const getEventSignupPresentation = (
  event: GuildEventLifecycleInput,
  now: Date,
) => {
  const closesAt = event.eventKind === "raid" ? event.signupCloseAt : null
  return {
    isOpen: isSignupOpen(event, now),
    closesAt,
    openUntil: closesAt ?? event.startAt,
  }
}
export const GUILD_EVENT_STATUS_LABELS: Record<GuildEventStatus, string> = {
  scheduled: "Scheduled",
  closed: "Closed",
  cancelled: "Cancelled",
  started: "Started",
  announcement_missing: "Announcement Missing",
  archived: "Archived",
}

export const GUILD_EVENT_SYNC_STATUS_LABELS: Record<
  GuildEventSyncStatus,
  string
> = {
  healthy: "Healthy",
  degraded: "Degraded",
}

export const GUILD_EVENT_RSVP_STATE_LABELS: Record<
  GuildEventRsvpState,
  string
> = {
  attending: "Attending",
  tentative: "Tentative",
  not_attending: "Not Attending",
}

export function getStatusLabel(status: GuildEventStatus): string {
  return GUILD_EVENT_STATUS_LABELS[status]
}

export function getSyncStatusLabel(syncStatus: GuildEventSyncStatus): string {
  return GUILD_EVENT_SYNC_STATUS_LABELS[syncStatus]
}

export function getRsvpLabel(
  state: GuildEventRsvpState | null | undefined,
): string {
  if (!state) return "No RSVP"
  return GUILD_EVENT_RSVP_STATE_LABELS[state]
}

export function getMemberRsvpSummary(
  state: GuildEventRsvpState,
  roleKey: WwmEventRoleKey | null | undefined,
): string {
  return `Your RSVP: ${getRsvpLabel(state)}${roleKey ? ` · ${getRoleLabel(roleKey)}` : ""}`
}

export function getRoleLabel(roleKey: WwmEventRoleKey): string {
  return WWM_EVENT_ROLE_CATALOG[roleKey]
}
